/**
 * 创建详细编辑器引导，通过宿主能力筛选步骤和临时展开界面。
 * @param context 文档能力与侧栏准备端口。
 * @returns 可继续追加任意数量步骤的配置。
 */
export function createEditorOnBoardingConfig(context: EditorOnBoardingContext): OnBoardingConfig {
  return {
    storageKey: 'puppycad.editor.onboarding.v2',
    backgroundOpacity: 0.6,
    tips: [
      {
        el: '[data-onboarding="main-menu"]',
        title: '欢迎使用 PuppyCAD',
        content: '从这里导入、导出文件，使用撤销与重做。以后也可以通过“帮助 → 新手引导”再次查看这份介绍。',
        tipPosition: 'bottom',
      },
      {
        el: '[data-onboarding="submit"]',
        title: '提交本次修改',
        content:
          '完成一个阶段的修改后，点击“提交”进入提交流程。提交后的版本可以在左侧“提交记录”中查看。',
        when: () => !context.isReadonly(),
      },
      {
        el: '[data-onboarding="editor-settings"]',
        title: '编辑器设置',
        content:
          '需要调整捕捉习惯时，从这里打开编辑器设置。设置项用于控制当前项目的捕捉行为；不确定某项作用时，可先保留默认设置。',
      },
      {
        el: '[data-onboarding="command-toolbar"]',
        title: '功能工具栏',
        content:
          '草图工具用于绘制轮廓，特征工具用于实体建模；装配体工作区还提供装配工具。工具是否可用取决于当前文档、选择对象和正在进行的操作。',
        tipPosition: 'bottom',
        when: () => !context.isReadonly(),
      },
      {
        el: '[data-onboarding="operation-hint"]',
        title: '不知道下一步时，看操作提示',
        content:
          '执行工具时，这里会提示当前操作需要的选择或输入。绘制期间按工具能力显示取消、撤销点和完成入口。',
        when: () => !context.isReadonly(),
      },
      {
        el: '[data-onboarding="viewport"]',
        title: '建模视口',
        content: '这里展示模型，也是选择对象、绘制草图和查看建模结果的工作区域。',
        tipPosition: 'right',
      },
      {
        el: '[data-onboarding="display-model"]',
        title: '切换模型显示方式',
        content:
          '这里提供“带边线上色”“上色”“不着色”等显示方式，便于分别观察模型外形和边线。',
      },
      {
        el: '[data-onboarding="fit-view"]',
        title: '快速找回整个模型',
        content:
          '模型太大、太小或移出画面时，使用“整屏显示全图”，让可见模型重新适合当前视口。退出引导后也可按 F 使用这个功能。',
      },
      {
        el: '[data-onboarding="section-view"]',
        title: '观察模型内部',
        content:
          '剖面视图可以使用基准面或平面实体面设置截面，帮助观察内部结构。',
      },
      {
        el: '[data-onboarding="view-orientation"]',
        title: '从标准方向检查模型',
        content: '视图定向提供前、后、左、右、上、下等观察方向。需要检查某一侧形状时，使用标准方向比反复拖动更方便。',
      },
      {
        el: '[data-onboarding="projection-tools"]',
        title: '正交与透视',
        content:
          '正交视图便于比较形状与平行关系；透视视图更接近日常观察物体的效果。根据当前观察目的选择合适的投影方式。',
      },
      {
        el: '[data-onboarding="manager-panel"]',
        title: '设计树与属性',
        content: '在设计树中查看模型结构和特征；执行命令时，在属性页中设置参数并确认或取消操作。',
        tipPosition: 'left',
      },
      {
        el: '[data-onboarding="history-panel"]',
        title: '提交记录：查看版本来龙去脉',
        content:
          '这里可查看提交说明、负责人、时间和版本关系；选择历史提交可进入预览，也可基于某个版本继续编辑。',
        tipPosition: 'right',
        onEnter: (step) => context.enterSidePanel('history', step),
      },
      {
        el: '[data-onboarding="branch-panel"]',
        title: '节点与分支：理解装配体的版本来源',
        content:
          '采纳版本表示父装配体当前使用的子节点版本，负责分支表示对应的编辑工作区；有权限时可进入负责分支或切换采纳版本。',
        tipPosition: 'right',
        when: () => context.canOpenNodeBranches(),
        onEnter: (step) => context.enterSidePanel('branch', step),
      },
      {
        el: '[data-onboarding="status-bar"]',
        title: '准备开始',
        content: '状态栏显示初始化状态与当前选中对象。点击“完成”，开始使用编辑器。',
        tipPosition: 'top',
      },
    ],
  };
}
